import { z } from "zod";
import { parseToolBody } from "./parseToolBody";
import type { ToolCallEntry } from "./useTurns";

export const TRIGGER_RUN_TOOL_NAME = "public_dataset_trigger_run";
export const LIST_PIPELINES_TOOL_NAME = "public_dataset_list_pipelines";

// Trigger args: dag_id plus any declared-param overrides the user already
// consented to in chat. Not exported — only `parseTriggerArgs` needs it.
const TriggerArgsSchema = z.object({
  dag_id: z.string().min(1),
  // A pipeline's declared params are not all booleans — the trigger tool
  // accepts any value matching the Param's own JSON type, so an unparsed
  // override would render a card with no approve/reject control.
  dag_params: z
    .record(z.string(), z.union([z.boolean(), z.number(), z.string(), z.array(z.string())]))
    .optional(),
});

export interface TriggerArgs {
  dagId: string;
  dagParams: Record<string, boolean | number | string | string[]>;
}

export function parseTriggerArgs(args: string): TriggerArgs | null {
  const view = parseToolBody(args);
  if (view.kind !== "json" || Array.isArray(view.value)) return null;
  const parsed = TriggerArgsSchema.safeParse(view.value);
  if (!parsed.success) return null;
  return { dagId: parsed.data.dag_id, dagParams: parsed.data.dag_params ?? {} };
}

// Not exported — only `resolveDagDisplayName` below needs it.
const ListedPipelineSchema = z.object({
  dag_id: z.string(),
  dag_display_name: z.string().optional(),
});

// Never trust the trigger call's own args for the display name (model
// input) — correlate the turn's list_pipelines result instead, mirroring
// `ConfirmIngestCard`'s `findProfile`. Last match wins on a re-list.
export function resolveDagDisplayName(
  toolCalls: readonly ToolCallEntry[] | undefined,
  dagId: string
): string | undefined {
  if (!toolCalls) return undefined;
  const listings = toolCalls.filter((tc) => tc.name === LIST_PIPELINES_TOOL_NAME && tc.result);
  for (let i = listings.length - 1; i >= 0; i--) {
    const view = parseToolBody(listings[i].result);
    if (view.kind !== "json" || !Array.isArray(view.value)) continue;
    for (const raw of view.value) {
      const parsed = ListedPipelineSchema.safeParse(raw);
      if (parsed.success && parsed.data.dag_id === dagId) {
        return parsed.data.dag_display_name;
      }
    }
  }
  return undefined;
}
